import { useState, useCallback, useEffect } from 'react';
import { Company, CompanyHelpers } from '../types/company';
import { CompanyService } from '../services/company'; // Pour les mocks
import companyApiService from '../services/companyApi'; // Pour l'API réelle
import { useToast } from './useToast';
import { useUser } from './useUser';
import { CompanyPersistence } from '../utils/companyPersistence';

// Création des instances de service
const mockCompanyService = new CompanyService();

interface UseCompanyOptions {
  enabled?: boolean;
  preferMock?: boolean; // Option pour préférer les données mockées (tests/développement)
}

export function useCompany(companyId: string | undefined, options: UseCompanyOptions = { enabled: true, preferMock: false }) {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState<boolean>(options.enabled ?? true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [usingMockData, setUsingMockData] = useState<boolean>(options.preferMock || false);
  
  // Utiliser les hooks
  const toast = useToast();
  const { user, updateProfile } = useUser();

  // Fonction pour synchroniser silencieusement avec le backend
  const syncWithBackend = useCallback(async (companyId: string, userId: string) => {
    try {
      console.log('🔄 Synchronisation silencieuse avec le backend...');
      const freshData = await companyApiService.getCompany(companyId);
      // ✨ Pas de conversion : CompanyResponse contient déjà TOUTES les données
      
      // Mettre à jour le cache et l'état
      CompanyPersistence.save(freshData as any, userId);
      setCompany(freshData as any);
      console.log('✅ Synchronisation terminée');
    } catch (error) {
      console.warn('⚠️ Échec de la synchronisation silencieuse:', error);
    }
  }, []);

  const fetchCompany = useCallback(async () => {
    console.log('🔍 useCompany.fetchCompany - Debug:', {
      companyId: companyId || 'NONE',
      enabled: options.enabled,
      hasUser: !!user?.id,
      userId: user?.id || 'NONE'
    });
    
    // Si aucun ID n'est fourni ou que options.enabled est false, on ne charge pas les données
    if (!companyId || !options.enabled) {
      console.log('❌ Pas de chargement:', {
        reasonNoCompanyId: !companyId,
        reasonDisabled: !options.enabled
      });
      setLoading(false);
      setCompany(null);
      return;
    }

    // Vérifier qu'on a un token pour les requêtes API
    const token = localStorage.getItem('auth0_token');
    if (!token && !options.preferMock) {
      console.log('❌ Pas de token pour charger les données de l\'entreprise depuis l\'API');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log(`🚀 Début du chargement de l'entreprise: ${companyId}`);
      
      // 1. Vérifier d'abord le cache local si on a un utilisateur
      if (user?.id) {
        const cachedCompany = CompanyPersistence.load(user.id);
        if (cachedCompany && cachedCompany.id === companyId) {
          console.log('📦 Entreprise chargée depuis le cache:', cachedCompany.id);
          setCompany(cachedCompany);
          setLoading(false);
          
          // Continuer en arrière-plan pour synchroniser avec le backend
          if (!options.preferMock) {
            setTimeout(() => syncWithBackend(companyId, user.id), 100);
          }
          return;
        }
      }
      
      let companyData: Company | null = null;
      
      if (!options.preferMock) {
        try {
          // Essayer d'abord d'obtenir les données depuis l'API
          console.log(`🌐 Tentative de récupération de l'entreprise depuis l'API: ${companyId}`);
          const apiData = await companyApiService.getCompany(companyId);
          // ✨ Pas de conversion : CompanyResponse = CreateCompanyRequest + id/dates
          companyData = apiData as any;
          setUsingMockData(false);
          
          // Sauvegarder en cache
          if (user?.id && companyData) {
            CompanyPersistence.save(companyData, user.id);
          }
        } catch (apiError) {
          console.warn(`⚠️ Erreur API, utilisation des données mockées: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
          // En cas d'échec de l'API, fallback sur les données mockées
          companyData = await mockCompanyService.getCompany(companyId);
          setUsingMockData(true);
        }
      } else {
        // Utilisation directe des données mockées si préférées
        console.log(`🧪 Utilisation préférée des données mockées pour l'entreprise: ${companyId}`);
        companyData = await mockCompanyService.getCompany(companyId);
        setUsingMockData(true);
      }
      
      setCompany(companyData);
      setError(null);
    } catch (err) {
      const error = err as Error;
      setError(error);
      console.error('❌ Erreur lors de la récupération des données de l\'entreprise:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, options.enabled, options.preferMock]);

  // Charger les données quand les paramètres changent
  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const createCompany = async (companyData: Partial<Company>) => {
    setIsUpdating(true);
    setError(null);
    
    try {
      console.log('🆕 Création d\'une nouvelle entreprise:', companyData);
      
      let newCompany: Company;
      
      // Essayer d'abord l'API, puis fallback sur le mock si nécessaire
      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        
        console.log('📤 Envoi de la nouvelle entreprise au backend...');
        // ✨ Envoi direct : pas de conversion
        const createdCompanyResponse = await companyApiService.createCompany(companyData as any);
        newCompany = createdCompanyResponse as any; // CompanyResponse contient TOUTES les données
        console.log('✅ Entreprise créée au backend avec succès');
        
        // Créer également dans le mock pour garder la cohérence
        await mockCompanyService.createCompany(companyData as Company);
      } catch (apiError) {
        console.warn(`⚠️ Impossible de créer via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        // En cas d'échec, création via le mock
        newCompany = await mockCompanyService.createCompany(companyData as Company);
        setUsingMockData(true);
      }
      
      setCompany(newCompany);
      
      // 💾 Sauvegarder en cache
      if (user?.id) {
        CompanyPersistence.save(newCompany, user.id);
        console.log('💾 Entreprise sauvegardée en cache');
      }
      
      // 🎯 Mettre à jour le profil utilisateur avec l'ID de la nouvelle entreprise
      try {
        await updateProfile({ companyId: newCompany.id });
        console.log('✅ Profil utilisateur mis à jour avec l\'ID de l\'entreprise:', newCompany.id);
      } catch (updateError) {
        console.warn('⚠️ Impossible de mettre à jour le profil utilisateur:', updateError);
        // Ne pas bloquer le processus, l'entreprise a été créée avec succès
      }
      
      // Afficher une notification de succès
      toast.showToast?.({
        type: 'success',
        title: 'Succès',
        message: 'Entreprise créée avec succès!'
      });
      
      return newCompany;
    } catch (err) {
      const error = err as Error;
      setError(error);
      console.error('❌ Erreur lors de la création de l\'entreprise:', error);
      
      toast.showToast?.({
        type: 'error',
        title: 'Erreur',
        message: `Échec de la création: ${error.message}`
      });
      
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const updateCompany = async (companyData: Partial<Company>) => {
    if (!companyId) {
      // Si pas d'ID, créer une nouvelle entreprise
      return await createCompany(companyData);
    }
    
    setIsUpdating(true);
    setError(null);
    
    try {
      console.log(`🔄 Mise à jour de l'entreprise ${companyId}:`, companyData);
      
      let updatedCompany: Company;
      
      // Essayer d'abord l'API, puis fallback sur le mock si nécessaire
      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        
        console.log('📤 Envoi des données au backend...');
        // ✨ Envoi direct : pas de conversion
        const updatedCompanyResponse = await companyApiService.updateCompany(companyId, companyData as any);
        updatedCompany = updatedCompanyResponse as any; // CompanyResponse contient TOUTES les données
        console.log('✅ Données envoyées au backend avec succès');
        
        // Mettre à jour également le mock pour garder la cohérence
        await mockCompanyService.updateCompany(companyId, companyData);
      } catch (apiError) {
        console.warn(`⚠️ Impossible de mettre à jour via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        // En cas d'échec, mise à jour via le mock
        updatedCompany = await mockCompanyService.updateCompany(companyId, companyData);
        setUsingMockData(true);
      }
      
      setCompany(updatedCompany);
      
      // 💾 Mettre à jour le cache
      if (user?.id) {
        CompanyPersistence.save(updatedCompany, user.id);
        console.log('💾 Entreprise mise à jour en cache');
      }
      
      // Afficher une notification de succès
      toast.showToast?.({ 
        type: 'success', 
        title: 'Mise à jour réussie', 
        message: 'Les informations de l\'entreprise ont été mises à jour.'
      });
      
      return updatedCompany;
    } catch (err) {
      const error = err as Error;
      setError(error);
      console.error('❌ Erreur lors de la mise à jour de l\'entreprise:', error);
      
      // Afficher une notification d'erreur
      toast.showToast?.({ 
        type: 'error', 
        title: 'Erreur', 
        message: `Échec de la mise à jour: ${error.message}`
      });
      
      return null; // Retourner null au lieu de throw pour une gestion d'erreur cohérente
    } finally {
      setIsUpdating(false);
    }
  };

  const uploadLogo = async (file: File) => {
    if (!companyId) return null;
    setIsUpdating(true);
    setError(null);
    
    try {
      console.log(`🖼️ Upload du logo pour l'entreprise ${companyId}`);
      
      let result;
      
      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        
        // Essayer d'abord l'API
        result = await companyApiService.uploadLogo(companyId, file);
        console.log('✅ Logo uploadé au backend avec succès');
        
        // Mettre à jour également le mock pour garder la cohérence
        await mockCompanyService.uploadLogo(companyId, file);
      } catch (apiError) {
        console.warn(`⚠️ Impossible d'uploader via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        // En cas d'échec, upload via le mock
        result = await mockCompanyService.uploadLogo(companyId, file);
        setUsingMockData(true);
      }
      
      setCompany(prev => prev ? { ...prev, logo: result.url } : null);
      
      // Afficher une notification de succès
      toast.showToast?.({ 
        type: 'success', 
        title: 'Upload réussi', 
        message: 'Le logo de l\'entreprise a été mis à jour.'
      });
      
      return result;
    } catch (err) {
      const error = err as Error;
      setError(error);
      console.error('❌ Erreur lors de l\'upload du logo:', error);
      
      // Afficher une notification d'erreur
      toast.showToast?.({ 
        type: 'error', 
        title: 'Erreur', 
        message: `Échec de l'upload du logo: ${error.message}`
      });
      
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const uploadOwnerCV = async (file: File) => {
    if (!companyId) return null;
    setIsUpdating(true);
    setError(null);
    
    try {
      console.log(`📄 Upload du CV pour le propriétaire de l'entreprise ${companyId}`);
      
      let result;
      
      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        
        // Essayer d'abord l'API
        result = await companyApiService.uploadOwnerCV(companyId, file);
        console.log('✅ CV uploadé au backend avec succès');
        
        // Mettre à jour également le mock pour garder la cohérence
        await mockCompanyService.uploadOwnerCV(companyId, file);
      } catch (apiError) {
        console.warn(`⚠️ Impossible d'uploader via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        // En cas d'échec, upload via le mock
        result = await mockCompanyService.uploadOwnerCV(companyId, file);
        setUsingMockData(true);
      }
      
      // Mise à jour du state avec le nouvel URL du CV
      // ✨ Mettre à jour le CV du propriétaire (dirigeant principal)
      setCompany(prev => {
        if (!prev) return null;
        return CompanyHelpers.updateOwnerCV(prev, result.url);
      });
      
      // Afficher une notification de succès
      toast.showToast?.({ 
        type: 'success', 
        title: 'Upload réussi', 
        message: 'Le CV a été mis à jour.'
      });
      
      return result;
    } catch (err) {
      const error = err as Error;
      setError(error);
      console.error('❌ Erreur lors de l\'upload du CV:', error);
      
      // Afficher une notification d'erreur
      toast.showToast?.({ 
        type: 'error', 
        title: 'Erreur', 
        message: `Échec de l'upload du CV: ${error.message}`
      });
      
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  // Fonction pour forcer le rafraîchissement des données
  const refreshCompany = () => {
    fetchCompany();
  };

  return {
    company,
    loading,
    isUpdating,
    error,
    usingMockData, // Nouvel indicateur pour savoir si on utilise les données mockées
    createCompany,
    updateCompany,
    uploadLogo,
    uploadOwnerCV,
    refreshCompany,
    fetchCompany
  };
}